/// <reference lib="dom" />
import * as d3 from "d3";
import type { ChartCtx, ChartLayer, ChartScale } from "../core/chart.ts";
import { ScaleIn, Single } from "./shared.ts";

export type HandleAxis = "xy" | "x" | "y";

export interface HandleDatum {
  id: string;
  x: number;
  y: number;
  color?: string;
  radius?: number;
  label?: string;
  labelColor?: string;
  sublabel?: string;
  axis?: HandleAxis;
  guide?: HandleAxis;
  filled?: boolean;
}

export interface HandlePosition {
  x: number;
  y: number;
}

export interface HandlesLayerOpts {
  id?: string;
  x?: ChartScale;
  y?: ChartScale;
  onDrag?: (id: string, position: HandlePosition) => void;
}

export interface HandlesLayer extends ChartLayer {
  set(handles: HandleDatum[]): void;
}

interface Guide {
  key: string;
  axis: "x" | "y";
  handle: HandleDatum;
}

export function HandlesLayer(opts: HandlesLayerOpts = {}): HandlesLayer {
  let handles: HandleDatum[] = [];
  let last: ChartCtx | null = null;
  let active: HandleDatum | null = null;
  let bound = false;

  const scales = (ctx: ChartCtx) => ({
    x: ScaleIn(ctx, "x", opts.x),
    y: ScaleIn(ctx, "y", opts.y),
  });

  function nearest(px: number, py: number): HandleDatum | null {
    if (!last || handles.length === 0) return null;
    const { x, y } = scales(last);
    let best: HandleDatum | null = null;
    let bestDist = Infinity;
    for (const h of handles) {
      const dist = Math.hypot(x(h.x) - px, y(h.y) - py);
      if (dist < bestDist) {
        best = h;
        bestDist = dist;
      }
    }
    return best;
  }

  function move(px: number, py: number) {
    if (!last || !active || !opts.onDrag) return;
    const { inner } = last.frame;
    const { x, y } = scales(last);
    const axis = active.axis ?? "xy";
    const cx = Math.max(inner.left, Math.min(inner.right, px));
    const cy = Math.max(inner.top, Math.min(inner.bottom, py));
    opts.onDrag(active.id, {
      x: axis === "y" ? active.x : x.invert(cx),
      y: axis === "x" ? active.y : y.invert(cy),
    });
  }

  return {
    id: opts.id ?? "handles",
    set(next) {
      handles = next;
      if (active) active = handles.find((h) => h.id === active!.id) ?? null;
    },
    render(root, ctx) {
      last = ctx;
      const { theme } = ctx;
      const { inner } = ctx.frame;
      const { x, y } = scales(ctx);

      const guides: Guide[] = handles.flatMap((handle) => {
        const g = handle.guide;
        if (!g) return [];
        const out: Guide[] = [];
        if (g !== "y") out.push({ key: `${handle.id}-x`, axis: "x", handle });
        if (g !== "x") out.push({ key: `${handle.id}-y`, axis: "y", handle });
        return out;
      });
      root.selectAll<SVGLineElement, Guide>("line.guide")
        .data(guides, (g) => g.key)
        .join("line")
        .attr("class", "guide")
        .attr("x1", (g) => (g.axis === "x" ? x(g.handle.x) : inner.left))
        .attr("x2", (g) => (g.axis === "x" ? x(g.handle.x) : inner.right))
        .attr("y1", (g) => (g.axis === "x" ? inner.top : y(g.handle.y)))
        .attr("y2", (g) => (g.axis === "x" ? inner.bottom : y(g.handle.y)))
        .attr("stroke", (g) => g.handle.color ?? theme.series)
        .attr("stroke-width", 1)
        .attr("stroke-opacity", theme.guideOpacity)
        .attr("stroke-dasharray", "3,2")
        .style("pointer-events", "none");

      if (opts.onDrag) {
        Single<SVGRectElement>(root, "rect", "hit")
          .attr("x", inner.left)
          .attr("y", inner.top)
          .attr("width", inner.width)
          .attr("height", inner.height)
          .attr("fill", "transparent")
          .style("cursor", "crosshair");
        if (!bound) {
          bound = true;
          const svg = () => (root.node() as SVGGElement).ownerSVGElement!;
          root.select<SVGRectElement>("rect.hit").call(
            d3.drag<SVGRectElement, unknown>()
              .filter((event) => !event.shiftKey && !event.button)
              .on("start", (event) => {
                const [px, py] = d3.pointer(event, svg());
                active = nearest(px, py);
                move(px, py);
              })
              .on("drag", (event) => {
                const [px, py] = d3.pointer(event, svg());
                move(px, py);
              })
              .on("end", () => {
                active = null;
              }),
          );
        }
      }

      const radius = (h: HandleDatum) => h.radius ?? 4;
      root.selectAll<SVGCircleElement, HandleDatum>("circle")
        .data(handles, (h) => h.id)
        .join("circle")
        .attr("cx", (h) => x(h.x))
        .attr("cy", (h) => y(h.y))
        .attr("r", radius)
        .attr("fill", (h) => (h.filled ? h.color ?? theme.series : theme.background))
        .attr("stroke", (h) => h.color ?? theme.series)
        .attr("stroke-width", (h) => (h.filled ? 0 : 1.5))
        .style("pointer-events", "none");

      root.selectAll<SVGTextElement, HandleDatum>("text.label")
        .data(handles.filter((h) => h.label), (h) => h.id)
        .join("text")
        .attr("class", "label")
        .attr("x", (h) => x(h.x))
        .attr("y", (h) => y(h.y) - radius(h) - 4)
        .attr("text-anchor", "middle")
        .attr("font-size", theme.labelSize)
        .attr("fill", (h) => h.labelColor ?? h.color ?? theme.series)
        .style("pointer-events", "none")
        .text((h) => h.label!);

      root.selectAll<SVGTextElement, HandleDatum>("text.sublabel")
        .data(handles.filter((h) => h.sublabel), (h) => h.id)
        .join("text")
        .attr("class", "sublabel")
        .attr("x", (h) => x(h.x) + radius(h) + 3)
        .attr("y", (h) => y(h.y) + 3)
        .attr("font-size", theme.fontSize)
        .attr("fill", (h) => h.color ?? theme.series)
        .attr("opacity", 0.7)
        .style("pointer-events", "none")
        .text((h) => h.sublabel!);
    },
  };
}
